import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

/*
 * Une langue n'est proposée au sélecteur qu'une fois sa couverture complète :
 * l'exposer plus tôt revient à promettre une langue et servir du français par
 * fallback. Les cinq locales sont à 100 % — le test de parité échoue si une
 * clé fr est ajoutée sans sa traduction, ce qui garde l'invariant vrai.
 */
export const SUPPORTED_LANGUAGES = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
] as const;

export type LanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

/*
 * Chargement à la demande : chaque locale pèse ~100 Ko compressés, et un
 * utilisateur n'en lit qu'une. Les importer toutes dans le bundle initial
 * faisait télécharger ~530 Ko de traductions inutilisées avant le premier
 * rendu. Vite isole chaque `import()` dans son propre fichier.
 */
const LOADERS: Record<LanguageCode, () => Promise<{ default: Record<string, unknown> }>> = {
  fr: () => import('./locales/fr.json'),
  en: () => import('./locales/en.json'),
  es: () => import('./locales/es.json'),
  it: () => import('./locales/it.json'),
  de: () => import('./locales/de.json'),
};

const DEFAULT_LANGUAGE: LanguageCode = 'fr';

/** `en-GB` → `en` ; une langue non gérée retombe sur le français. */
export function toSupportedLanguage(lng: string | undefined | null): LanguageCode {
  const base = (lng ?? '').toLowerCase().split('-')[0];
  return (SUPPORTED_LANGUAGES.find((l) => l.code === base)?.code ?? DEFAULT_LANGUAGE) as LanguageCode;
}

/** Charge le dictionnaire d'une langue si ce n'est pas déjà fait. */
export async function loadLanguage(lng: string): Promise<LanguageCode> {
  const code = toSupportedLanguage(lng);
  if (!i18n.hasResourceBundle(code, 'translation')) {
    const mod = await LOADERS[code]();
    i18n.addResourceBundle(code, 'translation', mod.default, true, true);
  }
  return code;
}

/** Change de langue en chargeant d'abord son dictionnaire : pas de clés brutes entre-temps. */
export async function changeLanguage(lng: string): Promise<void> {
  const code = await loadLanguage(lng);
  await i18n.changeLanguage(code);
}

/**
 * Résolue quand la langue détectée est chargée : `main.tsx` attend cette
 * promesse avant le premier rendu, sans quoi l'interface s'afficherait un
 * instant avec des clés brutes.
 */
export const i18nReady: Promise<void> = i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {},
    fallbackLng: DEFAULT_LANGUAGE,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
      lookupLocalStorage: 'carrierbridge:language',
    },
  })
  .then(async () => {
    const code = await loadLanguage(i18n.language);
    // Normalise `fr-FR` / `en-GB` en code géré (le sélecteur et les formateurs
    // comparent des codes à deux lettres).
    if (i18n.language !== code) await i18n.changeLanguage(code);
  });

/* La langue du document suit la langue active (a11y, moteurs, césure) —
   index.html fige `lang="fr"` avant l'hydratation seulement. */
i18n.on('languageChanged', (lng) => {
  document.documentElement.lang = lng;
});
if (typeof document !== 'undefined') {
  document.documentElement.lang = toSupportedLanguage(i18n.language);
}

export default i18n;
